import { Modal } from "../../UI/Modal/Modal.tsx";
import { Dialog } from "@headlessui/react";
import { OutlinedButton } from "@normasteaching/norma-ui";
import {
  PromoDTO,
  PromoBonusDTO,
} from "../../ProductService/NormasTeachingPromo/useAllPromos.ts";
import { useAllNormasTeachingProducts } from "../../ProductService/NormasTeachingProduct/useAllNormasTeachingProducts.ts";
import { useAllNormasTeachingBundles } from "../../ProductService/NormasTeachingBundle/useAllNormasTeachingBundles.ts";
import { Package, Layers } from "lucide-react";
import { NormasTeachingItemType } from "../../Stripe/StripeOrderDTO.ts";

function formatDate(dateStr?: string): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getPromoStatus(promo: PromoDTO): {
  isActive: boolean;
  reason: string;
} {
  const now = new Date();
  if (!promo.isActive)
    return { isActive: false, reason: "Disattivata manualmente" };
  if (promo.validFrom && now < new Date(promo.validFrom))
    return { isActive: false, reason: "Non ancora valida" };
  if (promo.validTo && now > new Date(promo.validTo))
    return { isActive: false, reason: "Scaduta" };
  return { isActive: true, reason: "Attiva" };
}

function BonusCard({ bonus }: { bonus: PromoBonusDTO }) {
  if (bonus.type === "all_you_can_speak") {
    return (
      <div className="p-3 bg-blue-50 rounded-lg space-y-1">
        <p className="text-sm font-semibold text-blue-800">All You Can Speak</p>
        <p className="text-sm text-blue-700">
          {bonus.lessonsCountedPerWeek} lezioni conteggiate a settimana
        </p>
        {(bonus.activeFrom || bonus.activeTo) && (
          <p className="text-xs text-blue-600">
            {bonus.activeFrom ? `Dal ${formatDate(bonus.activeFrom)}` : ""}
            {bonus.activeTo ? ` al ${formatDate(bonus.activeTo)}` : ""}
          </p>
        )}
      </div>
    );
  }

  if (bonus.type === "extra_lessons") {
    return (
      <div className="p-3 bg-green-50 rounded-lg space-y-1">
        <p className="text-sm font-semibold text-green-800">Lezioni Extra</p>
        <p className="text-sm text-green-700">+{bonus.extraLessons} lezioni</p>
      </div>
    );
  }

  return (
    <div className="p-3 bg-purple-50 rounded-lg space-y-1">
      <p className="text-sm font-semibold text-purple-800">Settimane Extra</p>
      <p className="text-sm text-purple-700">+{bonus.extraWeeks} settimane</p>
    </div>
  );
}

export function PromoDetailModal({
  promo,
  onClose,
}: {
  promo: PromoDTO;
  onClose: () => void;
}) {
  const status = getPromoStatus(promo);
  const { products } = useAllNormasTeachingProducts();
  const { bundles } = useAllNormasTeachingBundles();

  const hasApplicableProducts =
    promo.applicableProducts && promo.applicableProducts.length > 0;

  const getItemName = (
    itemType: NormasTeachingItemType,
    id: string,
  ): string => {
    if (itemType === NormasTeachingItemType.Product) {
      const product = products.find((p) => p._id === id);
      return product
        ? `${product.productName} - ${product.variantProductName}`
        : "Prodotto sconosciuto";
    } else {
      const bundle = bundles.find((b) => b._id === id);
      return bundle
        ? `${bundle.bundleName} - ${bundle.variantBundleName}`
        : "Bundle sconosciuto";
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Dettagli Promo"
      overridePanel={({ title, children }) => (
        <Dialog.Panel className="flex-1 md:flex-auto md:max-w-2xl transform overflow-hidden md:rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <Dialog.Title
            as="h3"
            className="text-lg font-medium leading-6 text-gray-900"
          >
            {title}
          </Dialog.Title>
          <div className="mt-4">{children}</div>
        </Dialog.Panel>
      )}
    >
      <div className="space-y-6 max-h-[70vh] overflow-y-auto max-w-2xl">
        {/* Informazioni generali */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
            Informazioni Generali
          </h3>
          {promo.description && (
            <div>
              <span className="font-medium text-gray-700">Descrizione:</span>
              <p className="text-gray-600">{promo.description}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="font-medium text-gray-700">Nome:</span>
              <p className="text-gray-600">{promo.name}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Stato:</span>
              <p
                className={`font-semibold ${status.isActive ? "text-green-600" : "text-red-600"}`}
              >
                {status.reason}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Valido da:</span>
              <p className="text-gray-600">{formatDate(promo.validFrom)}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Valido fino a:</span>
              <p className="text-gray-600">{formatDate(promo.validTo)}</p>
            </div>
          </div>
        </div>

        {/* Bonus */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
            Bonus ({promo.bonuses.length})
          </h3>
          <div className="space-y-2">
            {promo.bonuses.map((bonus, i) => (
              <BonusCard key={i} bonus={bonus} />
            ))}
          </div>
        </div>

        {/* Prodotti applicabili */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
            Prodotti Applicabili
          </h3>
          {hasApplicableProducts ? (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {promo.applicableProducts!.map((item) => (
                <div
                  key={`${item.itemType}-${item.id}`}
                  className="flex items-center gap-2 p-2 bg-gray-50 rounded"
                >
                  {item.itemType === NormasTeachingItemType.Product ? (
                    <Package size={16} className="text-blue-500" />
                  ) : (
                    <Layers size={16} className="text-purple-500" />
                  )}
                  <span className="text-sm">
                    {getItemName(item.itemType, item.id)}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      item.itemType === NormasTeachingItemType.Product
                        ? "bg-blue-100 text-blue-700"
                        : "bg-purple-100 text-purple-700"
                    }`}
                  >
                    {item.itemType === NormasTeachingItemType.Product
                      ? "Prodotto"
                      : "Bundle"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-sm text-green-800 font-medium">
                ✓ Questa promo è applicabile a tutti i prodotti e bundle
              </p>
            </div>
          )}
        </div>

        <div className="pt-4 border-t">
          <OutlinedButton onClick={onClose} label="Chiudi" />
        </div>
      </div>
    </Modal>
  );
}
